<template>
  <template v-if="!isReady">
    <slot
      v-if="$slots.loading"
      name="loading"
    />
    <slot
      v-else
      name="denied"
    />
  </template>
  <slot
    v-else-if="isDefaultShown"
    :allowed="allowed"
  />
  <slot
    v-else
    name="denied"
  />
</template>

<script setup lang="ts">
/**
 * Render-less authorisation component, registered globally as `<TwnCan>`.
 *
 *   <TwnCan I="view" a="pos.settings.configuration">
 *     <ConfigurationPanel />
 *     <template #denied><UpgradeHint /></template>
 *     <template #loading><Skeleton /></template>
 *   </TwnCan>
 *
 * Fail-closed: while the ability is not `ready`, a missing `#loading` slot falls
 * through to `#denied` (renders nothing) — never the default slot.
 *
 * There is deliberately no instance or field prop. The contract carries flat
 * `[action, subject]` rules only, so a `:this="order"` check could never do
 * anything but deny — instance-level answers ship as `can` flags on the resource
 * DTO instead. Better a missing prop than a prop that silently hides UI.
 */
import { computed } from 'vue'

import type { TwnCanProps, TwnCanSlots } from './TwnCan.types'

import { useCan } from '../composables/useCan'
import { useRbac } from '../composables/useRbac'

const props = defineProps<TwnCanProps>()

defineSlots<TwnCanSlots>()

const { can } = useCan()
const { isReady } = useRbac()

const isGranted = computed(() => {
  const subject = props.a ?? props.an

  if (!subject) {
    return false
  }

  return can(props.I, subject)
})

const allowed = computed(() => (props.not ? !isGranted.value : isGranted.value))

/** `passThrough` hands the answer to the default slot instead of choosing a slot by it. */
const isDefaultShown = computed(() => props.passThrough || allowed.value)
</script>
